import type { ServerParseError } from "@apollo/client";
import { ApolloClient, from, InMemoryCache } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import ApolloLinkTimeout from "apollo-link-timeout";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { authStore } from "stores/authStore";
import { serviceStore } from "stores/serviceStore";
import { userStore } from "stores/userStore";
import { history } from "utils/history";
import { IssueReporter } from "utils/issueReporter";
import { GraphQLError } from "graphql/error";
import { owningTeam } from "graphql/operationOwner";
import { responseOverrideLink } from "./devtools/responseOverrideLink";
import { reporterForStatus } from "./reporterForStatus";
import { getPreviewGatewayPath } from "./devtools/previewGatewayPath";
import { possibleTypes, typePolicies } from "./typePolicies";
import { API_ERROR_CODES, idpErrorReasons } from "./constants";
import { getCurrentProjectId, navigateToIdpError } from "./utils";
import { readUser } from "./user";
import { clearStoredUserData, logout } from "./logout";
import { isUnauthorized } from "./apolloErrorUtils";

const INVALID_TOKEN = /invalid token/i;
const INVALID_MFA_CODE = /invalid mfa code/i;
const PROJECT_DOESNT_EXIST = /no project with that id exists/i;
const INVALID_USERNAME_PASSWORD = /invalid username\/password combination/i;
const ACTIVATE_ACCOUNT = /must activate your account/i;
const NO_TOKEN = /no token/i;
const EMAIL_ALIAS_FORBIDDEN = /email aliases are not allowed/i;

const customFetch = async (_uri: RequestInfo | URL, options?: RequestInit) => {
  // You can parse the `operationName` from the body for debugging purposes.
  // const { operationName } = JSON.parse((options as any).body);

  const prefix = getPreviewGatewayPath() ?? ""; // dev-only preview gateway
  const res = await fetch(
    `${import.meta.env.VITE_API_HOSTNAME || ""}${prefix}/api/query`,
    {
      ...options,
      credentials: "include",
    },
  );
  // An unknown/torn-down preview still 200s with an HTML fallback page
  // instead of erroring, which Apollo would otherwise choke on parsing.
  if (!res.headers.get("content-type")?.includes("application/json")) {
    throw new Error(`Non-JSON response from ${prefix || "gateway"}`);
  }
  return res;
};

const uploadLink = createUploadLink({ fetch: customFetch });
const timeoutLink = new ApolloLinkTimeout(120000).concat(uploadLink);

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    // The route's serviceId, not the service: this link is built at module
    // scope and cannot use a hook, and the id is all it reports.
    const { serviceId } = serviceStore;
    const projectId = getCurrentProjectId(client);

    if (graphQLErrors) {
      const { location } = history;
      for (const error of graphQLErrors) {
        const { message, path, extensions } = error;

        // FusionAuth (our auth provider) is down: the backend attaches a
        // `fusionauth_down` code to any 5xx it returns, on whatever operation
        // happens to fail (LoginMfa on /login, CreateUser on /signup, GetUser
        // on /idp). We key off the code, not the operation, and send the user
        // to the full-page outage screen so they see the real reason instead of
        // the misleading SSO copy.
        if (extensions?.code === API_ERROR_CODES.FUSIONAUTH_DOWN_CODE) {
          // Report before navigating: the redirect (and the GetUser early-return
          // below) would otherwise skip the reporter at the end of the loop.
          // Shared fingerprint so an outage is one Sentry issue, not one per op.
          reporterForStatus(extensions?.status)(
            `[GraphQL]: (${extensions?.status}) FusionAuth down | Operation: ${operation.operationName}`,
            {
              source: "client",
              status: extensions?.status,
              code: API_ERROR_CODES.FUSIONAUTH_DOWN_CODE,
              operation: operation.operationName,
              fingerprint: [
                "graphql-error",
                API_ERROR_CODES.FUSIONAUTH_DOWN_CODE,
              ],
            },
          );
          navigateToIdpError({
            reason: idpErrorReasons.AUTH_SERVICE_DOWN,
            operation: operation.operationName,
          });
          // continue (not return) so sibling errors still reach their handlers.
          continue;
        }

        // IDP failure to log in
        if (
          operation.operationName === "GetUser" &&
          location.pathname === "/idp"
        ) {
          navigateToIdpError({ reason: idpErrorReasons.IDP_LOGIN_FAILED });
        }

        // `AuthProvider` runs GetUser to learn if a session is active. A 401
        // without a cached user means that the visitor is signed out: do not
        // log out or redirect. With a cached user, the session ended while it
        // was in use, so log out below. This runs before Apollo writes the
        // answer.
        const isGetUser401 =
          operation.operationName === "GetUser" && isUnauthorized(error);
        if (isGetUser401 && !readUser(client)) {
          // A user id that localStorage restored means that the session ended
          // without a logout: for example, the cookie expired. Clear the data
          // of that user, as a logout does. Keep the page to return to after
          // the login: it belongs to this tab, not to the user. During a
          // login, the login handles the answer.
          if (userStore.currentId && !authStore.inProgress) {
            clearStoredUserData({ keepRedirectUrl: true });
          }
          return;
        }
        if (
          isGetUser401 ||
          (isUnauthorized(error) &&
            (message?.match(INVALID_TOKEN) || message?.match(NO_TOKEN)))
        ) {
          const redirectUrl =
            history.location.pathname + history.location.search;
          void logout({ redirectUrl, reason: "sessionEnded" });
          break;
        }

        if (
          // e.g. "identity provider must be used for this email domain: https://example.com/idp/startSSO"
          message?.includes("provider must be used for this email domain: ")
        ) {
          try {
            // The user must login with an identity provider; redirect to the provided login URL
            const idpUrlString = message.split(
              "provider must be used for this email domain: ",
            )[1]; // e.g. "https://example.com/idp/startSSO"

            const idpUrl = new URL(idpUrlString);

            // don't redirect to URLs like "javascript:alert(1)"
            if (idpUrl?.protocol === "https:" || idpUrl?.protocol === "http:") {
              window.location.href = idpUrlString;
            }
          } catch {
            // ignore URL parsing errors
          }
        }

        if (
          ["GetPostgresParameters", "UpdateUser"].includes(
            operation.operationName,
          )
        ) {
          return forward(operation); // Retry once on failure
        }

        // Exception to ignore pg param restart error
        if (
          (
            [
              [500, "could not read parameters"],
              [400, "instance must be running to read parameters"],
              [404, "no Endpoint for that service id exists"],
            ] as Array<[number, string]>
          ).some(
            ([status, err]) =>
              extensions?.status === status && message?.includes(err),
          )
        ) {
          continue;
        }

        // We expect this error when a user tries to login with a MFA token,
        // but the user typed the incorrect digits.
        if (message?.match(INVALID_USERNAME_PASSWORD)) {
          authStore.setError(
            graphQLErrors.map((error) => new GraphQLError(error.message)),
          );
          continue;
        }

        if (message?.match(PROJECT_DOESNT_EXIST)) {
          // The project was deleted, or the user lost access to it. Drop the
          // cached projects, so that `/projects` reads a fresh list and opens
          // another project.
          client.cache.evict({ fieldName: "getAllProjects" });
          client.cache.gc();
          history.push("/projects");
          break;
        }

        if (isUnauthorized(error) && !message?.match(INVALID_MFA_CODE)) {
          authStore.setError({ message: "Unauthorized" });
          void logout({ reason: "sessionEnded" });
          break;
        }

        // This error happens a user tries to login before
        // activating their email. When we set the error in
        // authStore, it shows the error below the password
        // input.
        if (message?.match(ACTIVATE_ACCOUNT)) {
          authStore.setError({ message });
          return;
        }

        // Email aliases are forbidden unless it's a timescale or a tigerdata email
        if (message?.match(EMAIL_ALIAS_FORBIDDEN)) {
          authStore.setError({ message });
          return;
        }

        console.error(
          `[GraphQL]: (${extensions?.status}) Operation: ${operation.operationName} | Message: ${message}`,
        );

        reporterForStatus(extensions?.status)(
          `[GraphQL]: (${extensions?.status}) Operation: ${operation.operationName} | Message: ${message}`,
          {
            source: "client",
            status: extensions?.status,
            // Ownership, not fault: the team owning the failing field (from
            // the schema's @owner directive), stamped regardless of status
            // so alert rules route 4xx and 5xx alike.
            team: owningTeam(path, operation.query) ?? "backend",
            // e.g. EXPLORER_PG_UNAVAILABLE; lets alert rules exclude
            // customer faults (paused/unreachable database).
            ...(extensions?.code ? { code: extensions.code } : {}),
            path,
            operation: operation.operationName,
            serviceId,
            projectId,
            // Group per operation+status so 4xx warnings and 5xx errors form
            // separate issues (string reports otherwise share one synthetic stack).
            fingerprint: [
              "graphql-error",
              operation.operationName,
              String(extensions?.status ?? "unknown"),
            ],
          },
        );
      }
    } else if (networkError) {
      if (networkError.name === "AbortError") return;

      // Strange Typescript bug where it won't find ServerParseError even though
      // You can clearly see it in the @apollo/client/errors file:
      //
      // export type NetworkError = Error | ServerParseError | ServerError | null;
      //
      // Adding "timeout" because of it's added via ApolloLinkTimeout.
      const { statusCode, message, timeout, response, bodyText } =
        networkError as ServerParseError & {
          timeout?: number;
          response?: Response;
          bodyText?: string;
        };

      const responseStatus = statusCode ?? response?.status;
      const responseBodyLength = bodyText?.length;
      const responseBodySnippet = bodyText?.slice(0, 200);
      // No GraphQL payload to attribute here; the attempted operation's
      // owner is the closest ownership signal. Statusless browser faults
      // (offline, CORS) are demoted to warning in beforeSend regardless.
      const team = owningTeam(undefined, operation.query);

      console.error(
        `[Network]: (${responseStatus}) Operation: ${operation.operationName} | Message: ${message}`,
      );
      reporterForStatus(responseStatus)(
        `[Network]: (${responseStatus}) Operation: ${operation.operationName} | Message: ${message}`,
        {
          source: "network",
          error: networkError,
          ...(team ? { team } : {}),
          statusCode: responseStatus,
          responseBodyLength: responseBodyLength,
          responseBodySnippet: responseBodySnippet,
          operation: operation.operationName,
          serviceId,
          projectId,
          timeout: timeout,
          errorName: networkError.name,
          errorStack: networkError.stack,
          online: navigator.onLine,
          fingerprint: [
            "network-error",
            operation.operationName,
            String(responseStatus ?? "unknown"),
          ],
        },
      );
    } else {
      console.error(`[Unknown]: Operation: ${operation.operationName}`);
      IssueReporter.warning(
        `[Unknown]: Operation: ${operation.operationName}`,
        {
          source: "unknown",
          operation: operation.operationName,
          serviceId,
          projectId,
          fingerprint: ["unknown-error", operation.operationName],
        },
      );
    }

    return;
  },
);

export const client = new ApolloClient({
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-first",
      errorPolicy: "all",
    },
    query: {
      fetchPolicy: "cache-first",
      errorPolicy: "all",
    },
    mutate: {
      errorPolicy: "all",
    },
  },
  link: from([errorLink, responseOverrideLink, timeoutLink]),
  cache: new InMemoryCache({
    possibleTypes,
    typePolicies,
  }),
});
